import React, { useState } from "react"
import { EyeIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useImpersonation } from "@/lib/useImpersonation"

/**
 * Persistent bar shown while a superuser is viewing the app as someone else.
 *
 * Deliberately loud and full-width above the sidebar: the whole hazard of
 * impersonation is forgetting you are in it, and every gate in the app is
 * quietly reporting someone else's access while this is on screen.
 */
export function ImpersonationBanner() {
  const { isImpersonating, target, stopImpersonation } = useImpersonation()
  const [exiting, setExiting] = useState(false)

  if (!isImpersonating || !target) return null

  const handleExit = async () => {
    setExiting(true)
    try {
      await stopImpersonation()
    } finally {
      setExiting(false)
    }
  }

  return (
    <div className="flex shrink-0 items-center justify-between gap-3 border-b border-warning bg-warning px-3 py-2 text-sm text-warning dark:text-amber-100 sm:gap-4 sm:px-4">
      <div className="flex min-w-0 items-center gap-2">
        <EyeIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="min-w-0 break-words leading-snug sm:truncate">
          Viewing as{" "}
          <span className="font-semibold">{target.name || target.email}</span>
          {target.name ? (
            <span className="hidden opacity-70 sm:inline"> ({target.email})</span>
          ) : null}
          <span className="opacity-70"> — read-only</span>
        </span>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={handleExit}
        disabled={exiting}
        className="shrink-0 border-warning bg-background/60 hover:bg-background"
      >
        {exiting ? "Exiting…" : "Exit"}
      </Button>
    </div>
  )
}

export default ImpersonationBanner
